import { z } from 'zod';
import type { Sip } from '@sbclite/core';
import { validateSipProfileSrtpCryptoSuites } from '@sbclite/core/schemas';
import { SRTP_CRYPTO_SUITES } from '../sip-peer-editor/schema';

const DEFAULT_CRYPTO_SUITES = ['AES_CM_128_HMAC_SHA1_80'] as const;

const cryptoSuitesSchema = z.array(z.string().min(1)).default([...DEFAULT_CRYPTO_SUITES]);

const optionalSessionRefresherSchema = z.preprocess(
  (value) => (value === '' ? undefined : value),
  z.enum(['uas', 'uac', 'none']).nullable().optional(),
);

const optionalSdpOutboundProfileSchema = z.preprocess(
  (value) => (value === '' ? undefined : value),
  z.enum(['passthrough', 'carrier-basic-rtp']).nullable().optional(),
);

export const sipProfileFormSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  enabled: z.boolean().default(true),
  sessionTimerMode: z.enum(['disabled', 'supported', 'required']).default('supported'),
  sessionExpiresDefaultSeconds: z.number().int().positive().nullable().optional(),
  sessionExpiresMinSeconds: z.number().int().positive().nullable().optional(),
  sessionExpiresMaxSeconds: z.number().int().positive().nullable().optional(),
  sessionRefresherPreference: optionalSessionRefresherSchema,
  sessionTimerRejectBelowMin: z.boolean().default(true),
  registrarEnabled: z.boolean().default(false),
  registerExpiresDefaultSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMinSeconds: z.number().int().positive().nullable().optional(),
  registerExpiresMaxSeconds: z.number().int().positive().nullable().optional(),
  registerMaxContacts: z.number().int().positive().nullable().optional(),
  registerAllowWildcardContact: z.boolean().default(false),
  registerRequireContact: z.boolean().default(true),
  digestAuthEnabled: z.boolean().default(false),
  digestNonceLifetimeSeconds: z.number().int().positive().nullable().optional(),
  digestLockoutMaxAttempts: z.number().int().positive().nullable().optional(),
  digestLockoutWindowSeconds: z.number().int().positive().nullable().optional(),
  digestQop: z.enum(['auth', 'auth-int']).default('auth'),
  digestAlgorithm: z.enum(['MD5', 'SHA-256']).default('MD5'),
  srtpMode: z.enum(['disabled', 'optional', 'mandatory']).default('optional'),
  sdpStripIce: z.boolean().default(false),
  sdpOutboundProfile: optionalSdpOutboundProfileSchema,
  cryptoSuites: cryptoSuitesSchema,
}).superRefine((data, ctx) => {
  const validateTriple = (
    label: string,
    requireAll: boolean,
    min: number | null | undefined,
    defaultValue: number | null | undefined,
    max: number | null | undefined,
    minPath: 'sessionExpiresMinSeconds' | 'registerExpiresMinSeconds',
    defaultPath: 'sessionExpiresDefaultSeconds' | 'registerExpiresDefaultSeconds',
    maxPath: 'sessionExpiresMaxSeconds' | 'registerExpiresMaxSeconds',
  ) => {
    const hasMin = min != null;
    const hasDefault = defaultValue != null;
    const hasMax = max != null;
    if (!hasMin && !hasDefault && !hasMax && !requireAll) return;
    if (!hasMin || !hasDefault || !hasMax) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [minPath], message: requireAll ? `Required ${label} minimum value is missing` : `Provide all ${label} bounds together` });
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [defaultPath], message: requireAll ? `Required ${label} default value is missing` : `Provide all ${label} bounds together` });
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [maxPath], message: requireAll ? `Required ${label} maximum value is missing` : `Provide all ${label} bounds together` });
      return;
    }
    if (min != null && defaultValue != null && min > defaultValue) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [minPath], message: `${label} minimum must be less than or equal to default` });
    }
    if (defaultValue != null && max != null && defaultValue > max) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [defaultPath], message: `${label} default must be less than or equal to maximum` });
    }
  };

  validateTriple(
    'session expiry',
    data.sessionTimerMode === 'required',
    data.sessionExpiresMinSeconds,
    data.sessionExpiresDefaultSeconds,
    data.sessionExpiresMaxSeconds,
    'sessionExpiresMinSeconds',
    'sessionExpiresDefaultSeconds',
    'sessionExpiresMaxSeconds',
  );

  validateTriple(
    'REGISTER expires',
    data.registrarEnabled === true,
    data.registerExpiresMinSeconds,
    data.registerExpiresDefaultSeconds,
    data.registerExpiresMaxSeconds,
    'registerExpiresMinSeconds',
    'registerExpiresDefaultSeconds',
    'registerExpiresMaxSeconds',
  );

  if (data.registrarEnabled && data.registerMaxContacts == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['registerMaxContacts'], message: 'Registrar-enabled profiles require a maximum contact count' });
  }
  if (data.digestAuthEnabled && data.digestNonceLifetimeSeconds == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['digestNonceLifetimeSeconds'], message: 'Digest-auth-enabled profiles require a nonce lifetime' });
  }
  if (data.digestAuthEnabled && data.digestLockoutMaxAttempts == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['digestLockoutMaxAttempts'], message: 'Digest-auth-enabled profiles require a lockout attempt limit' });
  }
  if (data.digestAuthEnabled && data.digestLockoutWindowSeconds == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['digestLockoutWindowSeconds'], message: 'Digest-auth-enabled profiles require a lockout window' });
  }
  if (!validateSipProfileSrtpCryptoSuites(data.srtpMode, data.cryptoSuites.length)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cryptoSuites'], message: 'At least one SRTP crypto suite is required when SRTP is mandatory' });
  }
});

export type SipProfileFormData = z.infer<typeof sipProfileFormSchema>;

export type SipProfile = Sip.SipProfileInfo;

export function buildSipProfileDefaultValues(profile?: SipProfile | null): SipProfileFormData {
  return {
    name: profile?.name ?? '',
    description: profile?.description ?? '',
    enabled: profile?.enabled ?? true,
    sessionTimerMode: profile?.sessionTimerMode ?? 'supported',
    sessionExpiresDefaultSeconds: profile?.sessionExpiresDefaultSeconds ?? undefined,
    sessionExpiresMinSeconds: profile?.sessionExpiresMinSeconds ?? undefined,
    sessionExpiresMaxSeconds: profile?.sessionExpiresMaxSeconds ?? undefined,
    sessionRefresherPreference: profile?.sessionRefresherPreference ?? undefined,
    sessionTimerRejectBelowMin: profile?.sessionTimerRejectBelowMin ?? true,
    registrarEnabled: profile?.registrarEnabled ?? false,
    registerExpiresDefaultSeconds: profile?.registerExpiresDefaultSeconds ?? undefined,
    registerExpiresMinSeconds: profile?.registerExpiresMinSeconds ?? undefined,
    registerExpiresMaxSeconds: profile?.registerExpiresMaxSeconds ?? undefined,
    registerMaxContacts: profile?.registerMaxContacts ?? undefined,
    registerAllowWildcardContact: profile?.registerAllowWildcardContact ?? false,
    registerRequireContact: profile?.registerRequireContact ?? true,
    digestAuthEnabled: profile?.digestAuthEnabled ?? false,
    digestNonceLifetimeSeconds: profile?.digestNonceLifetimeSeconds ?? undefined,
    digestLockoutMaxAttempts: profile?.digestLockoutMaxAttempts ?? undefined,
    digestLockoutWindowSeconds: profile?.digestLockoutWindowSeconds ?? undefined,
    digestQop: profile?.digestQop ?? 'auth',
    digestAlgorithm: profile?.digestAlgorithm ?? 'MD5',
    srtpMode: profile?.srtpMode ?? 'optional',
    sdpStripIce: profile?.sdpStripIce ?? false,
    sdpOutboundProfile: profile?.sdpOutboundProfile ?? undefined,
    cryptoSuites: profile ? [...profile.cryptoSuites] : [...DEFAULT_CRYPTO_SUITES],
  };
}

export function moveCryptoSuite(suites: readonly string[], fromIndex: number, toIndex: number): string[] {
  const next = [...suites];
  const [item] = next.splice(fromIndex, 1);
  if (item === undefined) return next;
  next.splice(toIndex, 0, item);
  return next;
}

export function toCreateInput(data: SipProfileFormData): Sip.SipProfileCreateInput & { readonly cryptoSuites: string[] } {
  return {
    name: data.name ?? '',
    description: data.description?.trim() ? data.description.trim() : null,
    enabled: data.enabled ?? true,
    sessionTimerMode: data.sessionTimerMode ?? 'supported',
    sessionExpiresDefaultSeconds: data.sessionExpiresDefaultSeconds,
    sessionExpiresMinSeconds: data.sessionExpiresMinSeconds,
    sessionExpiresMaxSeconds: data.sessionExpiresMaxSeconds,
    sessionRefresherPreference: data.sessionRefresherPreference ?? null,
    sessionTimerRejectBelowMin: data.sessionTimerRejectBelowMin ?? true,
    registrarEnabled: data.registrarEnabled ?? false,
    registerExpiresDefaultSeconds: data.registerExpiresDefaultSeconds,
    registerExpiresMinSeconds: data.registerExpiresMinSeconds,
    registerExpiresMaxSeconds: data.registerExpiresMaxSeconds,
    registerMaxContacts: data.registerMaxContacts,
    registerAllowWildcardContact: data.registerAllowWildcardContact ?? false,
    registerRequireContact: data.registerRequireContact ?? true,
    digestAuthEnabled: data.digestAuthEnabled ?? false,
    digestNonceLifetimeSeconds: data.digestNonceLifetimeSeconds,
    digestLockoutMaxAttempts: data.digestLockoutMaxAttempts,
    digestLockoutWindowSeconds: data.digestLockoutWindowSeconds,
    digestQop: data.digestQop ?? 'auth',
    digestAlgorithm: data.digestAlgorithm ?? 'MD5',
    srtpMode: data.srtpMode ?? 'optional',
    sdpStripIce: data.sdpStripIce ?? false,
    sdpOutboundProfile: data.sdpOutboundProfile ?? undefined,
    cryptoSuites: (data.cryptoSuites ?? []).filter(Boolean),
  };
}

export function toUpdateInput(data: SipProfileFormData): Sip.SipProfileUpdateInput & { readonly cryptoSuites: string[] } {
  return toCreateInput(data);
}
